import { Actor } from "@dfinity/agent";
import { DGDG_ORIGIN } from "./constants.js";
import { createIcpAgent, normalizeIcpCanisterId } from "./icAgent.js";
const dgdgProxyIdlFactory = ({ IDL: I }) => I.Service({
    fetch_dgdg: I.Func([I.Text], [I.Variant({ ok: I.Text, err: I.Text })], []),
});
let runtimeProxyCanisterId = null;
/** Runtime override (e.g. from Registry env.json / loadConfig). */
export function setDgdgProxyCanisterId(id) {
    runtimeProxyCanisterId = id?.trim() ? id.trim() : null;
}
function readProxyCanisterId() {
    if (runtimeProxyCanisterId && normalizeIcpCanisterId(runtimeProxyCanisterId)) {
        return runtimeProxyCanisterId;
    }
    if (typeof import.meta !== "undefined") {
        const env = import.meta.env;
        const raw = env?.VITE_DGDG_PROXY_CANISTER_ID?.trim() ||
            env?.VITE_HOLDINGS_PROXY_CANISTER_ID?.trim();
        if (raw && normalizeIcpCanisterId(raw))
            return raw;
    }
    return null;
}
function readProxyHttpUrl() {
    if (typeof import.meta !== "undefined") {
        const env = import.meta.env;
        const raw = env?.VITE_DGDG_PROXY_URL?.trim();
        if (raw?.startsWith("https://"))
            return raw.replace(/\/$/, "");
    }
    return null;
}
/** Canister ID for the Bazaar DGDG read proxy (set via VITE_DGDG_PROXY_CANISTER_ID). */
export function getDgdgProxyCanisterId() {
    return readProxyCanisterId();
}
async function fetchViaCanisterProxy(path) {
    const canisterId = readProxyCanisterId();
    if (!canisterId) {
        throw new Error("DGDG proxy canister not configured");
    }
    const agent = await createIcpAgent();
    const actor = Actor.createActor(dgdgProxyIdlFactory, {
        agent,
        canisterId,
    });
    const result = await actor.fetch_dgdg(path);
    if ("err" in result) {
        throw new Error(`DGDG proxy: ${result.err}`);
    }
    return result.ok;
}
async function fetchViaHttpProxy(path) {
    const base = readProxyHttpUrl();
    if (!base)
        throw new Error("DGDG HTTP proxy URL not configured");
    const res = await fetch(`${base}?path=${encodeURIComponent(path)}`, { signal: AbortSignal.timeout(45_000) });
    if (!res.ok) {
        throw new Error(`DGDG HTTP proxy failed (${res.status})`);
    }
    return res.text();
}
async function fetchDirect(path) {
    const res = await fetch(`${DGDG_ORIGIN}${path}`, {
        headers: { Accept: "text/sveltekit-data" },
        signal: AbortSignal.timeout(35_000),
    });
    if (!res.ok) {
        throw new Error(`DGDG fetch failed (${res.status}): ${path}`);
    }
    return res.text();
}
/**
 * Fetch DGDG SvelteKit data. Prefers IC canister proxy, then optional HTTP proxy,
 * then direct fetch. In browser, falls back to a public CORS bridge if direct
 * fetch is blocked (until VITE_DGDG_PROXY_CANISTER_ID is deployed).
 */
export async function fetchDgdgText(path) {
    const proxyId = readProxyCanisterId();
    const inBrowser = typeof window !== "undefined";
    if (proxyId) {
        try {
            return await fetchViaCanisterProxy(path);
        }
        catch (proxyErr) {
            if (inBrowser) {
                try {
                    const target = encodeURIComponent(`${DGDG_ORIGIN}${path}`);
                    const bridge = await fetch(`https://api.allorigins.win/raw?url=${target}`, { signal: AbortSignal.timeout(45_000) });
                    if (bridge.ok)
                        return bridge.text();
                }
                catch {
                    /* fall through */
                }
            }
            throw proxyErr;
        }
    }
    if (readProxyHttpUrl()) {
        return fetchViaHttpProxy(path);
    }
    if (inBrowser) {
        try {
            return await fetchDirect(path);
        }
        catch (directErr) {
            const target = encodeURIComponent(`${DGDG_ORIGIN}${path}`);
            const bridge = await fetch(`https://api.allorigins.win/raw?url=${target}`, { signal: AbortSignal.timeout(45_000) });
            if (!bridge.ok) {
                throw directErr;
            }
            return bridge.text();
        }
    }
    return fetchDirect(path);
}
